import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function StorePage(): Node {
  return html`
    <div>
      <h1>Store</h1>
      <p><code>createStore</code> is a signal over an object with a convenient <code>.update()</code> method for partial merges. Use it for managing structured application state.</p>

      <h2>Basic Usage</h2>
      ${CodeBlock(`import { createStore } from 'onefold';

interface AppState {
  user: string | null;
  theme: 'light' | 'dark';
  count: number;
}

const store = createStore<AppState>({
  user: null,
  theme: 'light',
  count: 0,
});

// Read the full state
store()  // { user: null, theme: 'light', count: 0 }

// Partial update — merges with existing state
store.update({ count: 5 });
// { user: null, theme: 'light', count: 5 }

store.update({ user: 'Alice', theme: 'dark' });
// { user: 'Alice', theme: 'dark', count: 5 }`)}

      <h2>Reactivity</h2>
      <p>Store is a signal — use it in templates and effects just like <code>createSignal</code>:</p>
      ${CodeBlock(`import { createEffect, html } from 'onefold';

createEffect(() => {
  console.log('Theme changed:', store().theme);
});

function ThemeDisplay(): Node {
  return html\`
    <p>Current theme: \${() => store().theme}</p>
    <button onclick=\${() => store.update({
      theme: store().theme === 'light' ? 'dark' : 'light'
    })}>Toggle Theme</button>
  \`;
}`)}

      ${Callout('store.update() performs a shallow merge (like Object.assign). For deeply nested state, spread inner objects yourself.')}

      <h2>Replace vs Update</h2>
      ${CodeBlock(`// .update() — shallow merge (keeps other fields)
store.update({ count: 10 });

// .set() — full replacement (overwrites the entire object)
store.set({ user: null, theme: 'light', count: 0 });`)}

      <h2>API Reference</h2>
      <table>
        <tr><th>Method</th><th>Description</th></tr>
        <tr><td><code>store()</code></td><td>Read current state (subscribes in reactive context).</td></tr>
        <tr><td><code>store.set(value)</code></td><td>Replace the entire state object.</td></tr>
        <tr><td><code>store.update(partial)</code></td><td>Shallow merge partial into current state.</td></tr>
        <tr><td><code>store.peek()</code></td><td>Read current state without subscribing.</td></tr>
      </table>

      ${Playground(`import { html, mount, createStore, createComputed } from 'onefold';

function App() {
  // createStore: a signal over an object with .update() for partial merges
  const store = createStore({
    items: [],
    filter: 'all', // 'all' | 'active' | 'done'
    nextId: 1,
  });

  const input = { value: '' };

  // Derived counts from store
  const counts = () => {
    const { items } = store();
    return {
      total: items.length,
      active: items.filter(i => !i.done).length,
      done: items.filter(i => i.done).length,
    };
  };

  // Filtered items based on current filter
  const filtered = () => {
    const { items, filter } = store();
    if (filter === 'active') return items.filter(i => !i.done);
    if (filter === 'done') return items.filter(i => i.done);
    return items;
  };

  function addItem() {
    const text = input.value.trim();
    if (!text) return;
    const { items, nextId } = store();
    store.update({
      items: [...items, { id: nextId, text, done: false }],
      nextId: nextId + 1,
    });
    input.value = '';
    const el = document.querySelector('#store-input');
    if (el) el.value = '';
  }

  function toggleItem(id) {
    store.update({
      items: store().items.map(i => i.id === id ? { ...i, done: !i.done } : i),
    });
  }

  function removeItem(id) {
    store.update({ items: store().items.filter(i => i.id !== id) });
  }

  function setFilter(f) {
    store.update({ filter: f });
  }

  function clearDone() {
    store.update({ items: store().items.filter(i => !i.done) });
  }

  return html\`
    <div>
      <h3>Task Manager — createStore</h3>
      <div style="display:flex;gap:8px;margin-bottom:12px">
        <input id="store-input" placeholder="What needs doing?"
          oninput=\${(e) => { input.value = e.target.value; }}
          onkeydown=\${(e) => { if (e.key === 'Enter') addItem(); }}
          style="flex:1" />
        <button onclick=\${addItem}>Add</button>
      </div>
      <div style="display:flex;gap:8px;margin-bottom:12px">
        \${['all','active','done'].map(f => html\`
          <button onclick=\${() => setFilter(f)}
            style=\${() => store().filter === f ? 'background:#4f46e5;color:white;border-color:#4f46e5' : ''}
          >\${f}</button>
        \`)}
        <button onclick=\${clearDone} style="margin-left:auto;font-size:12px;color:#ef4444">Clear done</button>
      </div>
      <ul style="list-style:none;padding:0">
        \${() => filtered().map(item => html\`
          <li style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid #f1f5f9">
            <input type="checkbox" \${item.done ? 'checked' : ''} onchange=\${() => toggleItem(item.id)} />
            <span style=\${item.done ? 'text-decoration:line-through;color:#94a3b8' : ''}>\${item.text}</span>
            <button onclick=\${() => removeItem(item.id)} style="margin-left:auto;font-size:11px;color:#999;border:none;background:none;cursor:pointer">✕</button>
          </li>
        \`)}
      </ul>
      <p style="font-size:12px;color:#666;margin-top:8px">
        \${() => counts().active} active · \${() => counts().done} done · \${() => counts().total} total
        | filter: \${() => store().filter}
      </p>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'createStore — Task Manager with Filters')}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/state/persisted">Persisted Signals</a> — automatically sync state to localStorage</li>
        <li><a href="/data/resource">Resource</a> — reactive async data fetching</li>
      </ul>
    </div>
  `;
}
